import { createContext, useContext, useMemo } from 'react';
import {
  FieldValues,
  // eslint-disable-next-line no-restricted-imports -- this wrapper supplies the application navigation context
  FormProvider as FormProviderRHF,
  FormProviderProps as FormProviderPropsRHF,
} from 'react-hook-form';

import { NavigationFormContextValue } from '@/types/react-hook-form';

// Forms that use native RHF methods have no navigation guard. Give them neutral
// defaults here so a nested form does not inherit its parent's navigation state.
const setShouldBlockNavigationDefault: NavigationFormContextValue['setShouldBlockNavigation'] =
  () => undefined;

const defaultNavigationFormContext: NavigationFormContextValue = {
  shouldBlockNavigation: false,
  setShouldBlockNavigation: setShouldBlockNavigationDefault,
};

const NavigationFormContext = createContext<NavigationFormContextValue>(
  defaultNavigationFormContext
);

type FormProviderProps<
  TFieldValues extends FieldValues = FieldValues,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors RHF generic defaults
  TContext = any,
  TTransformedValues = TFieldValues,
> = FormProviderPropsRHF<TFieldValues, TContext, TTransformedValues> &
  Partial<NavigationFormContextValue>;

/**
 * Keeps Luminary's navigation guard separate from RHF's form state. RHF 7.87
 * forwards only its own provider props; our extra navigation fields no longer
 * reach consumers through the native context. Our useFormContext combines both
 * contexts so existing callers keep their navigation methods, scoped to this form.
 *
 * For example, import useForm, FormProvider, and useFormContext from
 * '@/components/react-hook-form'. Pass the result of useForm to
 * <FormProvider {...methods}>. A child reads setShouldBlockNavigation from
 * useFormContext() during render, then calls setShouldBlockNavigation(false)
 * after a successful save.
 * The native provider cannot pass that navigation override to the child.
 */
export function FormProvider<
  TFieldValues extends FieldValues,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors RHF generic defaults
  TContext = any,
  TTransformedValues = TFieldValues,
>({
  children,
  shouldBlockNavigation = false,
  setShouldBlockNavigation = setShouldBlockNavigationDefault,
  ...methods
}: FormProviderProps<TFieldValues, TContext, TTransformedValues>) {
  const navigationContext = useMemo(
    () => ({ shouldBlockNavigation, setShouldBlockNavigation }),
    [setShouldBlockNavigation, shouldBlockNavigation]
  );

  return (
    <NavigationFormContext.Provider value={navigationContext}>
      <FormProviderRHF {...methods}>{children}</FormProviderRHF>
    </NavigationFormContext.Provider>
  );
}

export function useNavigationFormContext(): NavigationFormContextValue {
  return useContext(NavigationFormContext);
}
